import { Component, OnInit } from '@angular/core';
import { PagesStore } from 'src/app/core/utils/pages-store/pages-store';
import { TicketHeader } from '../models/ticket-header';
import { mspTicketsSortColumns } from '../interfaces/msp-ticket-sort-columns';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import {
  ApiPagesStoreRequestData,
  PagesStoreRequestData,
} from 'src/app/core/utils/pages-store/pages-store-request-data';
import { PagesStoreResponse } from 'src/app/core/utils/pages-store/pages-store-response';
import { FormControl, FormGroup } from '@angular/forms';
import { ITicketFiltersForm } from '../interfaces/tickets-filter-form';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import { OneLineTextControlConfig } from 'src/app/features/reactive-forms-ui/models/one-line-text-control-config';
import { FiltersPanelLayoutConfig } from 'src/app/features/reactive-forms-ui/models/filters-panel-layout-config';
import { CheckboxTreeControlConfig } from 'src/app/features/reactive-forms-ui/models/checkbox-tree-control-config';
import { ManagedServicesRepository } from '../../services/managed-services-repository.service';
import { DatetimeRangeQuickPickerControlConfig } from 'src/app/features/reactive-forms-ui/models/datetime-range-quick-picker-control-config';
import { SegmentedControlConfig } from 'src/app/features/reactive-forms-ui/models/segmented-control-config';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { ManagedServicesSharedDataService } from '../../services/managed-services-shared-data.service';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { FeaturesFlagsV2Service } from 'src/app/core/services/features-flags-v2.service';
import { IMspBoard } from '../interfaces/msp-board';

@Component({
  selector: 'app-tickets-table-v2',
  templateUrl: './tickets-table-v2.component.html',
  styleUrls: ['./tickets-table-v2.component.scss'],
  standalone: false,
})
export class TicketsTableV2Component implements OnInit {
  store: PagesStore<TicketHeader>;

  /** The number of tickets to be displayed */
  numberOfTickets: number = 0;

  /** The index of the currently selected page */
  pageIndex: number = 0;

  /** The direction by which to sort the data */
  private sortDirection: string = 'DESC';

  /** The column by which to sort the tickets */
  private sortColumn: string = mspTicketsSortColumns.lastUpdated;

  /** The number of pages to simultaneously fetch from the DB */
  readonly pagesPerRequest: number = 1;

  /** The size of the pages for the table */
  readonly pageSize: number = 10;

  // store today's date
  today: Date = new Date();

  isBusinessUnitFlagEnabled = false;

  canSeeSitesFilter = false;

  mspBoards: IMspBoard[] = [];

  categoryItems = [];

  /** The columns to be displayed in the table */
  get tableColumns(): string[] {
    const columns = [
      'id',
      'statusName',
      'summary',
      'priorityName',
      'relationship',
      'dateEntered',
      'lastUpdated',
      'closedDate',
      'boardName',
      'siteName',
      ...(this.isBusinessUnitFlagEnabled ? ['businessUnitName'] : []),
      'contactFullName',
    ];
    return columns;
  }

  /** The data source for the table */
  dataSource: TicketHeader[] = [];

  filtersForm: FormGroup = new FormGroup({
    ticketNumber: new FormControl(''),
    statusesGroup: new FormControl(['open']),
    summary: new FormControl(''),
    businessUnitName: new FormControl(null),
    priorities: new FormControl(null),
    relationship: new FormControl(['parent']),
    creation: new FormControl(null),
    updated: new FormControl(null),
    closed: new FormControl(null),
    category: new FormControl(null),
    sites: new FormControl(null),
    contact: new FormControl(''),
  });

  valuesMap: ITicketFiltersForm = this.filtersForm.value;

  filtersConfigMap: Map<string, IFormControlConfig> = new Map<string, IFormControlConfig>([
    [
      'ticketNumber',
      new OneLineTextControlConfig({
        label: 'Ticket Number',
        reviewLabel: 'Ticket Number',
        placeholder: 'Enter ticket number',
        showCancelIcon: true,
        trailingIcon: 'icon_search_gray',
      }),
    ],
    [
      'statusesGroup',
      new SegmentedControlConfig({
        collapsibleTitle: 'Statuses',
        reviewLabel: 'Statuses',
        options: [
          { name: 'Open', value: 'open', icon: 'visibility_off', selectedIcon: 'visibility' },
          { name: 'Waiting', value: 'waiting', icon: 'visibility_off', selectedIcon: 'visibility' },
          { name: 'Closed', value: 'closed', icon: 'visibility_off', selectedIcon: 'visibility' },
        ],
        multiSelect: true,
        growAuto: true,
      }),
    ],
    [
      'summary',
      new OneLineTextControlConfig({
        collapsibleTitle: 'Summary',
        reviewLabel: 'Summary',
        placeholder: 'Search Summary',
        showCancelIcon: true,
        trailingIcon: 'icon_search_gray',
      }),
    ],
    [
      'businessUnitName',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Business Unit',
        searchPlaceholder: 'Filter Business Units',
        showSearchBar: true,
        classifications: ['Business Units'],
        items: [],
      }),
    ],
    [
      'priorities',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Priorities',
        searchPlaceholder: 'Filter Priorities',
        showSearchBar: true,
        classifications: ['Priorities'],
        items: [],
      }),
    ],
    [
      'relationship',
      new SegmentedControlConfig({
        collapsibleTitle: 'Ticket Relation',
        reviewLabel: 'Ticket Relation',
        options: [
          { name: 'Parent', value: 'parent', icon: 'visibility_off', selectedIcon: 'visibility' },
          { name: 'Child', value: 'child', icon: 'visibility_off', selectedIcon: 'visibility' },
        ],
        multiSelect: true,
        growAuto: true,
      }),
    ],
    [
      'creation',
      new DatetimeRangeQuickPickerControlConfig({
        collapsibleTitle: 'Creation',
        reviewLabel: 'Creation',
        startDateTimeLabel: 'Start date',
        endDateTimeLabel: 'End date',
        showTimeInputs: false,
        showCancelButton: true,
      }),
    ],
    [
      'updated',
      new DatetimeRangeQuickPickerControlConfig({
        collapsibleTitle: 'Updated',
        reviewLabel: 'Updated',
        startDateTimeLabel: 'Start date',
        endDateTimeLabel: 'End date',
        showTimeInputs: false,
        showCancelButton: true,
      }),
    ],
    [
      'closed',
      new DatetimeRangeQuickPickerControlConfig({
        collapsibleTitle: 'Closed',
        reviewLabel: 'Closed',
        startDateTimeLabel: 'Start date',
        endDateTimeLabel: 'End date',
        showTimeInputs: false,
        showCancelButton: true,
      }),
    ],
    [
      'category',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Board',
        showSelectAll: true,
        classifications: ['Category'],
        shouldAddParent: true,
        items: [],
      }),
    ],
    [
      'sites',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Sites',
        showSelectAll: true,
        showSearchBar: true,
        searchPlaceholder: 'Filter Sites',
        classifications: ['Sites'],
        shouldAddParent: true,
        items: [],
      }),
    ],
    [
      'contact',
      new OneLineTextControlConfig({
        collapsibleTitle: 'Contact',
        reviewLabel: 'Contact',
        placeholder: 'Search Contact',
        showCancelIcon: true,
        trailingIcon: 'icon_search_gray',
      }),
    ],
  ]);

  filtersLayoutConfig: FiltersPanelLayoutConfig;

  /** Flag to indicate if the tickets are being fetched */
  get isFetchingTickets(): boolean {
    return this.store.isFetchingData;
  }

  /** Subscriptions collection for cleanup */
  private subscriptions = new Subscription();

  constructor(
    private http: HttpClient,
    private managedServiceRepository: ManagedServicesRepository,
    private route: ActivatedRoute,
    private managedServicesSharedDataService: ManagedServicesSharedDataService,
    private permissionsService: PermissionsService,
    private featuresFlagsV2Service: FeaturesFlagsV2Service,
  ) {
    this.store = new PagesStore(
      `${environment.restUrl}/v3/tickets`,
      'GET',
      this.http,
      5,
      this.mapHeaders,
      this.pagesPerRequest,
      undefined,
      this.mapPropertiesFn,
    );
    this.subscriptions.add(
      this.featuresFlagsV2Service.featuresFlags$.subscribe((flags) => {
        this.isBusinessUnitFlagEnabled = flags.some((f) => f.feature === 'business-unit' && f.isActive);
      }),
    );
    const viewAllPermission = this.permissionsService.hasPermission('managed-services', 'Tickets', 'view-all');
    const viewBusinessUnitPermission = this.permissionsService.hasPermission(
      'managed-services',
      'Tickets',
      'view-business-unit',
    );
    const viewSitePermission = this.permissionsService.hasPermission('managed-services', 'Tickets', 'view-site');
    this.canSeeSitesFilter = viewAllPermission || viewBusinessUnitPermission || viewSitePermission;

    let elementIds = [
      'ticketNumber',
      'statusesGroup',
      'summary',
      'priorities',
      'relationship',
      'creation',
      'updated',
      'closed',
      'category',
      'businessUnitName',
      'sites',
      'contact',
    ];
    let elementsNotLoaded = ['priorities', 'category', 'businessUnitName', 'sites'];
    // Remove company sites filters if the user doesn't have permissions to view them
    if (!this.canSeeSitesFilter) {
      this.filtersConfigMap.delete('sites');
      elementIds = elementIds.filter((id) => id !== 'sites');
      elementsNotLoaded = elementsNotLoaded.filter((id) => id !== 'sites');
    }

    // Remove business unit filters if the feature is not enabled
    if (!this.isBusinessUnitFlagEnabled || (!viewAllPermission && !viewBusinessUnitPermission)) {
      this.filtersConfigMap.delete('businessUnitName');
      elementIds = elementIds.filter((id) => id !== 'businessUnitName');
      elementsNotLoaded = elementsNotLoaded.filter((id) => id !== 'businessUnitName');
    }

    this.filtersLayoutConfig = new FiltersPanelLayoutConfig({
      elementIds,
      elementsWithoutCollapsible: ['ticketNumber', 'statusesGroup'],
      title: 'Ticket Filters',
      elementsNotLoaded,
    });
  }

  ngOnInit(): void {
    const boards$ = this.managedServiceRepository.getMspAccountBoards();
    const params$ = this.route.queryParams;

    const combined = combineLatest([boards$, params$]).subscribe(([boards, params]) => {
      // Boards are guaranteed to be loaded before proceeding
      this.mspBoards = boards;
      this.categoryItems = this.mspBoards.reduce((acc, board) => {
        const category = acc.find((item) => item.id === board.category.toLowerCase());
        if (category) {
          category.children.push({
            id: String(board.id),
            displayName: board.name,
            children: [],
          });
        } else {
          acc.push({
            id: board.category.toLowerCase(),
            displayName: board.category,
            children: [
              {
                id: String(board.id),
                displayName: board.name,
                children: [],
              },
            ],
          });
        }
        return acc;
      }, []);

      // Now categoryItems is always populated when this runs
      const hasFilters = Object.keys(this.managedServicesSharedDataService.getFilters()).length > 0;
      if (params['refresh']) {
        if (hasFilters) {
          this.setFiltersFromSharedService();
        } else {
          this.fetchTickets(true);
        }
      } else {
        this.fetchTickets();
      }
    });

    this.subscriptions.add(combined);

    this.store.currentPageChanges.subscribe((currentPageData) => {
      this.dataSource = currentPageData.data ?? [];
      this.numberOfTickets = currentPageData.count;
    });
  }

  /** Called for event on the MatSort */
  onSortEvent(sortEvent: Sort) {
    const direction = sortEvent.direction === 'asc' ? 'ASC' : 'DESC';
    const sortColumn = mspTicketsSortColumns[sortEvent.active];

    // Do nothing if there are no direction or sortColumn changes
    if (direction === this.sortDirection && sortColumn === this.sortColumn) {
      return;
    }

    this.sortDirection = direction;
    this.sortColumn = sortColumn;
    this.pageIndex = 0;
    this.fetchTickets();
  }

  /** Called when there is an event on the paginator */
  onPageChange(pageEvent: PageEvent) {
    this.pageIndex = pageEvent.pageIndex;
    this.fetchTickets();
  }

  fetchTickets(shouldRefresh: boolean = false) {
    if (shouldRefresh) {
      this.store.clearCache();
    }

    this.store.requestPage(this.buildRequestData());
    this.setFormControlsConfigMinDates();
    this.setFormControlPriorities();
    this.setFormControlCategories();
    if (this.canSeeSitesFilter) {
      this.setFormCompanySites();
    }
    if (this.isBusinessUnitFlagEnabled) {
      this.setFormControlBusinessUnits();
    }
  }

  /** Removes the given [id] from the [elementsNotLoaded] array */
  removeIdFromNotLoadedElements(id: string) {
    this.filtersLayoutConfig.elementsNotLoaded.splice(this.filtersLayoutConfig.elementsNotLoaded.indexOf(id), 1);
  }

  setFormControlsConfigMinDates() {
    const formCreationDate = this.filtersConfigMap.get('creation') as DatetimeRangeQuickPickerControlConfig;

    const formUpdatedDate = this.filtersConfigMap.get('updated') as DatetimeRangeQuickPickerControlConfig;

    const formClosedDate = this.filtersConfigMap.get('closed') as DatetimeRangeQuickPickerControlConfig;

    formCreationDate.maxDate = this.today;
    formCreationDate.endDateTime = this.today.toISOString();

    formUpdatedDate.maxDate = this.today;
    formUpdatedDate.endDateTime = this.today.toISOString();

    formClosedDate.maxDate = this.today;
    formClosedDate.endDateTime = this.today.toISOString();
  }

  setFormControlPriorities() {
    this.managedServiceRepository.getMspPriorities().subscribe((priorities) => {
      const formPriorities = this.filtersConfigMap.get('priorities') as CheckboxTreeControlConfig;

      this.filtersConfigMap.set(
        'priorities',
        new CheckboxTreeControlConfig({
          ...formPriorities,
          items: priorities
            .map((priority) => ({
              id: String(priority.id),
              displayName: priority.name,
              children: [],
            }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName)),
        }),
      );

      this.removeIdFromNotLoadedElements('priorities');
    });
  }

  setFormControlBusinessUnits() {
    this.managedServiceRepository.getBusinessUnits().subscribe((businessUnits) => {
      const formBusinessUnits = this.filtersConfigMap.get('businessUnitName') as CheckboxTreeControlConfig;

      this.filtersConfigMap.set(
        'businessUnitName',
        new CheckboxTreeControlConfig({
          ...formBusinessUnits,
          items: businessUnits.map((bu) => ({
            id: bu,
            displayName: bu,
            children: [],
          })),
        }),
      );

      this.removeIdFromNotLoadedElements('businessUnitName');
    });
  }

  setFormControlCategories() {
    const formCategories = this.filtersConfigMap.get('category') as CheckboxTreeControlConfig;

    this.filtersConfigMap.set(
      'category',
      new CheckboxTreeControlConfig({
        ...formCategories,
        items: this.categoryItems,
      }),
    );

    this.removeIdFromNotLoadedElements('category');
  }

  /** Updates the values map and fetches the tickets */
  updateValuesMap(valuesMap: ITicketFiltersForm) {
    this.valuesMap = valuesMap;
  }

  applyFilters() {
    this.pageIndex = 0;
    this.fetchTickets(true);
  }

  clearFilters() {
    this.pageIndex = 0;
    this.filtersForm.reset();
    this.valuesMap = this.filtersForm.value;
    this.fetchTickets(true);
  }

  /** Builds the request data or params for the API */
  private buildRequestData(): PagesStoreRequestData {
    const payload = {
      module: 'managed-services',
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection === 'ASC' ? 'ASC' : 'DESC',
    } as const;

    if (this.valuesMap.ticketNumber) {
      payload['id'] = this.valuesMap.ticketNumber;
    }

    if (this.valuesMap.statusesGroup?.length > 0 && this.valuesMap.statusesGroup?.length < 3) {
      payload['statusesGroup'] = this.valuesMap.statusesGroup.join(';');
    }

    if (this.valuesMap.summary) {
      payload['summary'] = this.valuesMap.summary;
    }

    if (this.valuesMap.priorities?.['Priorities']?.values?.length) {
      payload['priorityIds'] = this.valuesMap.priorities['Priorities'].values.join(';');
    }

    if (this.valuesMap.creation?.start) {
      payload['createdAfter'] = this.valuesMap.creation.start.toISOString();
    }

    if (this.valuesMap.creation?.end) {
      payload['createdBefore'] = this.valuesMap.creation.end.toISOString();
    }

    if (this.valuesMap.updated?.start) {
      payload['updatedAfter'] = this.valuesMap.updated.start.toISOString();
    }

    if (this.valuesMap.updated?.end) {
      payload['updatedBefore'] = this.valuesMap.updated.end.toISOString();
    }

    if (this.valuesMap.category?.['default']?.values?.length > 0) {
      payload['boardIds'] = this.valuesMap.category['default'].values.join(';');
    } else {
      payload['boardIds'] = this.mspBoards.map((board) => board.id).join(';');
    }

    if (this.valuesMap.closed?.start) {
      payload['closedAfter'] = this.valuesMap.closed.start.toISOString();
    }

    if (this.valuesMap.closed?.end) {
      payload['closedBefore'] = this.valuesMap.closed.end.toISOString();
    }

    if (this.valuesMap.contact) {
      payload['contact'] = this.valuesMap.contact;
    }

    if (this.valuesMap.relationship?.length === 1) {
      payload['relationship'] = this.valuesMap.relationship[0];
    }

    if (this.valuesMap.sites?.['Sites']?.values?.length) {
      payload['siteIds'] = this.valuesMap.sites['Sites'].values.join(';');
    }

    if (this.isBusinessUnitFlagEnabled && this.valuesMap.businessUnitName?.['Business Units']?.values?.length) {
      payload['businessUnitNames'] = this.valuesMap.businessUnitName['Business Units'].values.join(';');
    }

    return payload;
  }

  private mapHeaders(rawResponse: PagesStoreResponse<any>): PagesStoreResponse<any> {
    return {
      data: rawResponse.data,
      count: rawResponse.count,
    };
  }

  private mapPropertiesFn(object: ApiPagesStoreRequestData): {
    [key: string]: any;
  } {
    const $limit = 10;
    const $page = object.pageIndex + 1;
    const { sortColumn, sortDirection } = object;
    const $orderBy = `${sortColumn} ${sortDirection}`;

    //delete the field with key equals to pageSize, pageIndex, sortColumn, sortDirection, pageOffset
    const newRequestData = { ...object };

    delete newRequestData.pageSize;
    delete newRequestData.pageIndex;
    delete newRequestData.sortColumn;
    delete newRequestData.sortDirection;
    delete newRequestData.pageOffset;

    return {
      ...newRequestData,
      $limit,
      $page,
      $orderBy,
    };
  }

  private setFiltersFromSharedService() {
    this.filtersForm.reset();
    const filters = this.managedServicesSharedDataService.getFilters();

    if (filters['statusesGroup']?.length > 0) {
      this.filtersForm.patchValue({ statusesGroup: [filters['statusesGroup'][0]] });
    }

    if (Object.keys(filters).includes('boardNames')) {
      const boardIds = this.mspBoards
        .filter((board) => filters['boardNames'].includes(board.name))
        .map((board) => String(board.id));

      const boardCategory = filters['boardNames'][0] === 'Help Desk' ? 'request' : 'incident';

      this.filtersForm.patchValue({
        category: {
          Category: {
            labels: [boardCategory],
            parentsNotSelected: [boardCategory],
            values: [boardCategory],
          },
          default: {
            labels: filters['boardNames'],
            values: boardIds,
          },
        },
      });
    }

    if (Object.keys(filters).includes('priorityIds')) {
      this.filtersForm.patchValue({
        priorities: {
          Priorities: {
            values: [...filters['priorityIds'].map((id: number) => id.toString())],
          },
        },
      });
    }

    if (Object.keys(filters).includes('summary')) {
      this.filtersForm.patchValue({ summary: filters['summary'] });
    }

    if (Object.keys(filters).includes('closedAfter') && Object.keys(filters).includes('closedBefore')) {
      this.filtersForm.patchValue({
        closed: {
          start: new Date(filters['closedAfter']),
          end: new Date(filters['closedBefore']),
        },
      });
    }

    if (Object.keys(filters).includes('createdAfter') && Object.keys(filters).includes('createdBefore')) {
      this.filtersForm.patchValue({
        creation: {
          start: new Date(filters['createdAfter']),
          end: new Date(filters['createdBefore']),
        },
      });
    }

    if (Object.keys(filters).includes('relationship')) {
      this.filtersForm.patchValue({ relationship: [filters['relationship']] });
    }

    this.valuesMap = this.filtersForm.value;
    this.fetchTickets();
  }

  setFormCompanySites() {
    this.managedServiceRepository.getCompanySites().subscribe((sites) => {
      const formSites = this.filtersConfigMap.get('sites') as CheckboxTreeControlConfig;

      this.filtersConfigMap.set(
        'sites',
        new CheckboxTreeControlConfig({
          ...formSites,
          items: sites
            .map((site) => ({
              id: String(site.id),
              displayName: site.name,
              children: [],
            }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName)),
        }),
      );

      this.removeIdFromNotLoadedElements('sites');
    });
  }
}
